import { ChevronRight, Tag, X } from 'lucide-react'
import type { FC } from 'react'

import { Button } from '@/components/button/button'
import { Card, CardContent, CardFooter } from '@/components/card/card'
import { Divider } from '@/components/divider/divider'
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel } from '@/components/form/form'
import { Input } from '@/components/input/input'
import { MultiSelect } from '@/components/multi-select/multi-select'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/select/select'

import { DOCUMENT_BODY_ELEMENT } from '@/lib/constants/platform'
import { cn } from '@/lib/utils'

import { defaultCategories } from '../../../../../applicants/ui/case-dialogs/constants'
import type { EditCaseDialogProps } from '../edit-case-dialog'
import { useContent } from './use-content'

export type ContentProps = Omit<EditCaseDialogProps, 'open'>

export const Content: FC<ContentProps> = ({ aCase, applicantId, onClose }) => {
  const {
    dataListCategory,
    form,
    formErrors,
    categoryId,
    categoryName,
    assignees,
    defaultAssignees,
    isPendingCreateCategory,
    isPendingEditCase,
    handleCategoryNameSave,
    handleSubmit,
  } = useContent({
    aCase,
    applicantId,
    onClose,
  })

  return (
    <Card>
      <Form {...form}>
        <form>
          <CardContent className="p-6">
            <div className="space-y-4">
              {categoryId !== 'add-category' && (
                <FormField
                  control={form.control}
                  rules={{
                    required: true,
                    validate: (value) => {
                      if (value === 'add-category') {
                        return 'Required'
                      }
                    },
                  }}
                  name="categoryId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Category*</FormLabel>
                      <Select onValueChange={field.onChange} value={categoryId} disabled={!applicantId}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={<span className="text-faint-foreground">Select</span>} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="flex-1">
                          <SelectGroup>
                            {dataListCategory?.map((category) => (
                              <SelectItem key={category.id} value={String(category.id)}>
                                {category.path_name}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                          <SelectSeparator />
                          <SelectGroup>
                            {defaultCategories
                              .filter((x) => {
                                const isDuplicate = dataListCategory?.some((y) => y.path_name === x.label)
                                return !isDuplicate
                              })
                              .map((category, i) => (
                                <SelectItem key={i} value={String(category.id)}>
                                  {category.label}
                                </SelectItem>
                              ))}
                          </SelectGroup>
                          <SelectItem value="add-category">
                            Add a category
                            <div className="absolute bottom-0 right-3 top-0 flex items-center justify-center">
                              <ChevronRight className="text-muted-foreground" size={16} />
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>Categorize cases for efficient management.</FormDescription>
                    </FormItem>
                  )}
                />
              )}

              {categoryId === 'add-category' && (
                <div className="space-y-2">
                  <FormField
                    control={form.control}
                    rules={{
                      required: true,
                      validate: () => {
                        if (categoryId === 'add-category') {
                          return 'Required'
                        }
                      },
                    }}
                    name="categoryName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Category*</FormLabel>
                        <FormControl>
                          <Card className="shadow">
                            <CardContent className="p-0">
                              <div className="flex items-center">
                                <Tag className="ml-4 text-muted-foreground" size={16} />
                                <Input
                                  className="flex-1 border-0 placeholder:text-faint-foreground"
                                  placeholder="Enter a category name"
                                  {...field}
                                />
                                <div
                                  className="cursor-pointer p-2"
                                  onClick={() => {
                                    form.resetField('categoryId')
                                  }}
                                >
                                  <X size={16} />
                                </div>
                              </div>
                              <div className="h-[1px] w-full bg-border" />
                              <div className="p-5">
                                <Button
                                  disabled={categoryName.trim() === '' || isPendingCreateCategory}
                                  className="w-full"
                                  type="button"
                                  onClick={handleCategoryNameSave}
                                >
                                  Save
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        </FormControl>
                        <FormDescription>Categorize cases for efficient management.</FormDescription>
                      </FormItem>
                    )}
                  />
                </div>
              )}

              <FormItem>
                <FormLabel>Case Number*</FormLabel>
                <FormControl>
                  <Input className="bg-muted placeholder:text-faint-foreground" value={aCase.case_number} disabled />
                </FormControl>
              </FormItem>

              <FormField
                control={form.control}
                name="tags"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tags</FormLabel>
                    <FormControl>
                      <Input
                        className="placeholder:text-faint-foreground"
                        placeholder="e.g. H1B, Green Card, etc."
                        {...field}
                      />
                    </FormControl>
                    <FormDescription className={cn(!!formErrors.tags?.message && 'text-red-500')}>
                      {formErrors.tags?.message ?? 'Assign one or more tags to the case, separated by commas.'}
                    </FormDescription>
                  </FormItem>
                )}
              />

              <Divider className="!mb-4 !mt-6" />

              {/* Case settings */}
              <FormField
                control={form.control}
                name="assignees"
                render={({ field: { value, onChange } }) => (
                  <FormItem>
                    <FormLabel>Assignees</FormLabel>
                    <FormControl>
                      <MultiSelect
                        asChild
                        portalContainer={DOCUMENT_BODY_ELEMENT}
                        defaultValues={defaultAssignees}
                        items={assignees}
                        placeholder="Select assignees"
                        maxCount={3}
                        value={value}
                        onValueChange={(selectedMembers) => onChange(selectedMembers)}
                      />
                    </FormControl>
                    <FormDescription>You can add multiple assignees.</FormDescription>
                  </FormItem>
                )}
              />
            </div>
          </CardContent>

          {/* SUBMIT */}
          <CardFooter>
            <Button type="button" disabled={!!formErrors.categoryId || isPendingEditCase} onClick={handleSubmit}>
              Save change
            </Button>
          </CardFooter>
        </form>
      </Form>
    </Card>
  )
}
